/* ag-set-renders.js - set-piece previews in a custom finish (Ansen 2026-10-16, canvas MUpB4vs5v52DZuwhEbZYWJ).

   When a shopper generates a custom-finish preview of one piece (Hershey in Golden Walnut), the pieces of its
   matching set (Winsor, Penfold) get a preview in the same finish at the same moment, and every place that shows
   those pieces uses it: the product page set card (ag-set-attach.js), the cart drawer's "Complete the set" card
   (cart-drawer.js) and the cart line itself (agcf-cart.js paints a line's _preview).

   The render service (arc-finish-render, behind the /apps/trade/render app proxy) takes &set=1&lead=<handle>: a set
   piece never counts toward the shopper's daily previews, and it is refused (429 set_refused) unless the shopper
   pressed Generate on the lead piece for the same finish pair, or the lead's render already exists. A refusal is
   quiet: the card keeps its stock picture.

   Winsor and Penfold are different pieces at each width, so their previews are per width (&size=, key __w<inches>).

   window.AGSR:
     want(o, {probe}) -> Promise<url|null>; o = { handle, p, a, side, size, lead }. Cached, de-duplicated, never
                throws. probe: only look (free); without it the service makes the preview if it may.
     known(o)   -> url or null, without asking the service.
     predict(o) -> the url the service will serve for o once it is made (for a cart line added mid-render), or null.
     isReady(url), waitImage(url) -> Promise<url|null>: whether / when a (predicted) preview url can be shown.
     refused(o) -> true when the last real request for o ended with no picture (in the last hour); a new want() retries.
     codes(s)   -> { p, a } from agcf's _finish_codes ("P=LS26082;A=9176;S=R").
     inches(s), sideOf(s), keyOf(o)
   Events on window: "agsr:ready" { key, url } when a preview lands. agcf.js fires "agcf:generate" { handle, p, a,
   name } the moment Generate is pressed; listeners start the set pieces then, not after the main preview. */
(function () {
  "use strict";
  if (window.AGSR) return;

  var PER_WIDTH = { "winsor-office-shelves-storage-with-suit-closet": 1, "penfold-office-credenza-cabinet-sideboard": 1 };
  var me = document.currentScript;
  var BASE = ((me && me.getAttribute("data-b")) || "").trim().replace(/\/+$/, "");
  var VKEY = "agcf8:vid";          /* shared with agcf.js and agcf-track.js: ONE visitor id */
  var SKEY = "agsr:v1";
  var MAX_KEEP = 80;
  /* IMGBASE: where the service serves previews (fly.toml IMG_BASE); relearnt from every url it answers */
  var MEM = {}, ORDER = [], FLIGHT = {}, IMGBASE = "https://arc-finish-render.fly.dev/img/";
  var PRED = {}, READY = {}, WAITS = {};    /* predicted url -> key; url -> loaded once; url -> Promise */
  /* round 3: when each preview started being made (key or url -> ms), kept with the store, so a loading bar drawn again
     (the cart closed and reopened, another page) continues where the render really is instead of starting over */
  var STARTED = {};
  /* 2026-10-27: key -> ms when a real request for it ended with no picture (refused, failed, timed out); kept with the
     store for an hour like STARTED, so another page does not pick a bar back up for it */
  var NO = {};

  /* kept in localStorage (round 3): a set piece's preview must still be there when the shopper opens that piece's own
     page, in this tab or another (agcf.js shows them first in that page's gallery: forHandle) */
  var STORE = null;
  try { STORE = window.localStorage; STORE.getItem(SKEY); } catch (e) { STORE = null; }
  try {
    var saved = STORE ? JSON.parse(STORE.getItem(SKEY) || "null") : null;
    if (saved && saved.m && saved.o) { MEM = saved.m; ORDER = saved.o; IMGBASE = saved.b || IMGBASE; STARTED = saved.s || {}; NO = saved.n || {}; }
  } catch (e) { /* storage blocked: memory only */ }
  function save() {
    while (ORDER.length > MAX_KEEP) delete MEM[ORDER.shift()];
    var old = Date.now() - 3600000;
    for (var sk in STARTED) if (STARTED[sk] < old) delete STARTED[sk];
    for (var nk in NO) if (NO[nk] < old) delete NO[nk];
    try { if (STORE) STORE.setItem(SKEY, JSON.stringify({ m: MEM, o: ORDER, b: IMGBASE, s: STARTED, n: NO })); } catch (e) { /* memory only */ }
  }

  function base() {
    if (BASE) return BASE;
    var n = document.querySelector("[data-agcf-gen-base]");
    var v = n ? (n.getAttribute("data-agcf-gen-base") || "").trim() : "";
    return v ? v.replace(/\/+$/, "") : "/apps/trade/render";
  }
  function vid() {
    var v = "";
    try { v = localStorage.getItem(VKEY) || ""; } catch (e) { v = ""; }
    return /^[A-Za-z0-9-]{8,64}$/.test(v) ? v : "";
  }
  function inches(s) {
    if (/^\s*\d+\s*$/.test(String(s || ""))) return +s;          /* already inches ("72") */
    var m = /(\d+)\s*[′'’]\s*(?:(\d+)\s*[″"”])?/.exec(String(s || ""));
    return m ? (+m[1]) * 12 + (+(m[2] || 0)) : 0;
  }
  function sideOf(s) { return /left/i.test(s || "") ? "left" : /right/i.test(s || "") ? "right" : "base"; }
  function codes(s) {
    var out = { p: "", a: "" };
    String(s || "").split(";").forEach(function (kv) {
      var i = kv.indexOf("=");
      if (i < 0) return;
      var k = kv.slice(0, i).trim(), v = kv.slice(i + 1).trim();
      if (k === "P") out.p = v;
      else if (k === "A") out.a = v;
    });
    return out;
  }
  function width(o) { return PER_WIDTH[o.handle] ? inches(o.size) : 0; }
  function keyOf(o) {
    var w = width(o);
    return o.handle + "__" + o.p + "__" + (o.a || "none") + "__" + (o.side || "base") + (w ? "__w" + w : "");
  }
  function known(o) { return (o && o.handle && o.p && MEM[keyOf(o)]) || null; }
  /* the service names a render <img base>/wm1_<handle>__<p>__<a>__<side>__1024px__p2[__w<in>].png; learnt from the
     first url it serves, never guessed before that */
  function predict(o) {
    if (!o || !o.handle || !o.p || !IMGBASE) return null;
    var w = width(o);
    var url = IMGBASE + "wm1_" + o.handle + "__" + o.p + "__" + (o.a || "none") + "__" + (o.side || "base") + "__" + IMGBASE_TAIL + (w ? "__w" + w : "") + ".png";
    PRED[url] = keyOf(o);
    if (!STARTED[PRED[url]]) started(url);
    return url;
  }
  /* a cart line can be added while its preview is still being made (Ansen 2026-10-16: "carry the flashing and the
     progress bar into it, even after they click Add"): it carries the predicted url, and these tell the cart when that
     picture exists. The service answers a missing picture 404 no-store, so the same url is simply asked again. */
  function isReady(url) {
    if (!url) return false;
    if (READY[url]) return true;
    for (var k in MEM) if (MEM[k] === url) return true;
    return false;
  }
  function waitImage(url, maxMs) {
    if (!url) return Promise.resolve(null);
    if (isReady(url)) return Promise.resolve(url);
    if (WAITS[url]) return WAITS[url];
    if (given(keyFor(url))) return Promise.resolve(null);
    if (!STARTED[url] && !(PRED[url] && STARTED[PRED[url]])) started(url);
    var deadline = Date.now() + (maxMs || 300000);
    var key = PRED[url];
    var flight = !!(key && FLIGHT[key]);
    var first = flight ? FLIGHT[key] : Promise.resolve(null);
    var p = first.then(function (u) {
      if (u === url) return url;
      if (!u && flight) return null;         /* the request behind this picture gave up: stop, never poll 5 minutes */
      return (function tryLoad() {
        return preload(url).then(function (ok) {
          if (ok) return url;
          if (Date.now() > deadline) return null;
          return wait(4000).then(tryLoad);
        });
      })();
    }).then(function (u) {
      delete WAITS[url];
      if (u) {
        READY[url] = true;
        try { window.dispatchEvent(new CustomEvent("agsr:ready", { detail: { key: key || "", url: url } })); } catch (e) { /* old browsers */ }
      }
      return u;
    }, function () { delete WAITS[url]; return null; });
    WAITS[url] = p;
    return p;
  }
  var IMGBASE_TAIL = "1024px__p2";
  function learn(url) {
    var m = /^(.*\/)wm1_[^/]*?__(\d+px__p\d+)(?:__w\d+)?\.png$/.exec(url || "");
    if (m) { IMGBASE = m[1]; IMGBASE_TAIL = m[2]; }
  }
  function abs(u) { return /^https?:/i.test(u || "") ? u : base() + (u || ""); }
  function remember(key, url) {
    if (!url) return;
    learn(url);
    if (!MEM[key]) ORDER.push(key);
    MEM[key] = url;
    delete NO[key];
    save();
    try { window.dispatchEvent(new CustomEvent("agsr:ready", { detail: { key: key, url: url } })); } catch (e) { /* old browsers */ }
  }
  function preload(url) {
    return new Promise(function (res) {
      var im = new Image();
      im.onload = function () { res(url); };
      im.onerror = function () { res(null); };
      im.src = url;
    });
  }
  function wait(ms) { return new Promise(function (res) { setTimeout(res, ms); }); }

  /* probe = &derive=1: the service answers a saved (or free flip-derived) preview, and 204 for anything it would have
     to make, spending nothing - used for a combo the shopper picked but has not pressed Generate on */
  function post(o, probe) {
    var w = width(o);
    var qs = "?primary=" + encodeURIComponent(o.p) + (o.a ? "&accent=" + encodeURIComponent(o.a) : "") +
      "&src=" + encodeURIComponent(o.side || "base") + "&handle=" + encodeURIComponent(o.handle) +
      "&vid=" + encodeURIComponent(vid()) +
      /* round 4 (Ansen 2026-10-17, board 12A): a cart line's own preview, started by the cart: free, &cart=1 */
      (o.cart ? "&cart=1" : "&set=1" + (o.lead ? "&lead=" + encodeURIComponent(o.lead) : "")) +
      (w ? "&size=" + w : "") + (probe ? "&derive=1" : "") + "&b=agsr";
    return fetch(base() + "/jobs" + qs, { method: "POST" }).then(function (r) {
      if (r.status === 204) return null;
      if (r.status === 429) return r.json().then(function (j) { return { refused: true, j: j }; }, function () { return { refused: true }; });
      if (!r.ok) return null;
      return r.json();
    });
  }
  function poll(id, deadline) {
    if (Date.now() > deadline) return Promise.resolve(null);
    return wait(2500).then(function () {
      return fetch(base() + "/jobs/" + encodeURIComponent(id) + "?b=agsr&vid=" + encodeURIComponent(vid()));
    }).then(function (r) { return r.ok ? r.json() : null; }, function () { return null; }).then(function (j) {
      if (j && j.status === "done" && j.url) return j;
      if (j && j.status === "failed") return null;
      return poll(id, deadline);
    });
  }
  /* a set submit can land a moment before the lead's own submit is on record: retry a refusal twice */
  function attempt(o, tries, probe) {
    return post(o, probe).then(function (j) {
      if (j && j.refused) {
        if (!probe && tries > 0 && j.j && j.j.error === "set_refused") return wait(2500).then(function () { return attempt(o, tries - 1, probe); });
        return null;
      }
      if (j && j.status === "done" && j.url) return j;
      var id = j && (j.job || j.id);
      return id ? poll(id, Date.now() + 300000) : null;
    }, function () { return null; });
  }
  var PROBE = {};
  function want(o, opts) {
    if (!o || !o.handle || !o.p) return Promise.resolve(null);
    var key = keyOf(o);
    if (MEM[key]) return Promise.resolve(MEM[key]);
    var probe = !!(opts && opts.probe);
    var map = probe ? PROBE : FLIGHT;
    if (FLIGHT[key]) return FLIGHT[key];        /* a real request answers a probe too */
    if (map[key]) return map[key];
    if (!probe) { delete NO[key]; started(key); }
    var p = attempt(o, 2, probe).then(function (j) {
      if (!j || !j.url) return null;
      return preload(abs(j.url));
    }).then(function (url) {
      delete map[key];
      if (url) remember(key, url);
      else if (!probe) gaveUp(key);
      return url;
    }, function () { delete map[key]; if (!probe) gaveUp(key); return null; });
    map[key] = p;
    return p;
  }
  function pending(o) { return !!(o && FLIGHT[keyOf(o)]); }
  /* 2026-10-27 (Ansen: "the Idyll ... progresses a little bit, like the progress bar, and then it stops, and then
     there's nothing"): a real request that ended with no picture forgets every start time behind it - its key and any
     predicted url of it, on this page or saved by another - so the card keeps its stock picture and no bar restarts */
  function gaveUp(key) {
    NO[key] = Date.now();
    delete STARTED[key];
    for (var sk in STARTED) if (keyFor(sk) === key) delete STARTED[sk];
    save();
  }
  function given(key) { return !!(key && NO[key]); }
  /* a key, or a (predicted) preview url -> its key: <base>wm1_<handle>__<p>__<a>__<side>__1024px__p2[__w<in>].png */
  function keyFor(id) {
    if (!id) return "";
    if (PRED[id]) return PRED[id];
    var m = /wm1_([^/]+?)__\d+px__p\d+(__w\d+)?\.png(?:[?#].*)?$/.exec(id);
    return m ? m[1] + (m[2] || "") : id;
  }
  function started(id, t) {
    if (!id || STARTED[id]) return;
    STARTED[id] = t || Date.now();
    save();
  }
  /* ms since the preview behind a key or (predicted) url started being made; 0 when unknown */
  function elapsed(id) {
    if (given(keyFor(id))) return 0;
    var t = id ? (STARTED[id] || (PRED[id] && STARTED[PRED[id]])) : 0;
    return t ? Math.max(0, Date.now() - t) : 0;
  }

  /* round 3: this product's set-piece previews for its own page's gallery, in agcf.js's key shape
     (handle__p__a__side[__w<in>]), oldest first and the page's current width last, so agcf's boot restore (which puts
     each new slide on top) shows the newest - and the width the page opened at - first. A desk's preview shows on the
     page's selected side: the service made the other side as a free mirror at the same time. */
  function forHandle(handle, side, w) {
    if (!handle) return [];
    var pre = handle + "__", seen = {}, rest = [], mine = [];
    for (var i = 0; i < ORDER.length; i++) {
      var k = ORDER[i], url = MEM[k];
      if (!url || k.indexOf(pre) !== 0) continue;
      var parts = k.split("__");
      if ((parts[3] === "left" || parts[3] === "right") && (side === "left" || side === "right") && parts[3] !== side) {
        url = url.replace("__" + parts[3] + "__", "__" + side + "__");
        parts[3] = side;
        k = parts.join("__");
      }
      if (seen[k]) continue;
      seen[k] = true;
      (w && parts[4] === "w" + w ? mine : rest).push({ key: k, url: url });
    }
    return rest.concat(mine);
  }

  /* ---- round 4 (Ansen 2026-10-16) ----
     1. "If I select a 4 ft width for the Penfold and refresh, it goes back to 5 ft 3 in - memorize those": the width and
        return a shopper picks for a set piece, on the product page card or the drawer card, kept per piece. */
  var CKEY = "ag-set-choice:v1";
  function choices() {
    try { return JSON.parse((STORE && STORE.getItem(CKEY)) || "{}") || {}; } catch (e) { return {}; }
  }
  function choice(handle) { return (handle && choices()[handle]) || null; }
  function rememberChoice(handle, c) {
    if (!handle || !c) return;
    var all = choices();
    all[handle] = { size: c.size || "", ret: c.ret || "", t: Date.now() };
    var ks = Object.keys(all).sort(function (a, b) { return (all[b].t || 0) - (all[a].t || 0); });
    for (var i = 40; i < ks.length; i++) delete all[ks[i]];
    try { if (STORE) STORE.setItem(CKEY, JSON.stringify(all)); } catch (e) { /* memory only */ }
  }
  /* 2. "If I click the ARC GROVE logo the complete-the-set card is gone": the drawer's set card prices a custom combo
        with window.AGCF.comboPrice, which only agcf.js (product pages) defines. Off a product page this loads the same
        rule's data (assets/ag-combo-price.json, built by theme-test/set-custom-finishes/build_combo_price.py) and defines
        it - the exact arithmetic of agcf.js a8ComboPrice / a8PriceFor / a8PctFor / a8GlossOf. */
  var CP = null, CP_LOAD = null;
  function cpUrl() {
    var u = me ? (me.getAttribute("data-cp") || "") : "";
    if (u) return u;
    var src = me ? (me.getAttribute("src") || "") : "";
    return src ? src.replace(/ag-set-renders\.js/, "ag-combo-price.json") : "";
  }
  /* PANTONE round (2026-10-16): a PANTONE code (PMS-<number>-<RRGGBB>) is eligible and priced like a gloss
     finish - the same rule agcf.js applies through its material ("Painted gloss") */
  var PMS_RE = /^PMS-[A-Za-z0-9_]+-[0-9A-Fa-f]{6}$/;
  function comboPrice(codes, unitCents, stockName) {
    var mp = /(?:^|;)P=([^;]+)/.exec(codes || "");
    var p = mp && (CP.cs[mp[1]] || PMS_RE.test(mp[1])) ? mp[1] : null;
    if (!p) return null;
    var ma = /(?:^|;)A=([^;]+)/.exec(codes || "");
    var a = ma && (CP.cs[ma[1]] || PMS_RE.test(ma[1])) ? ma[1] : null;
    var stock = stockName ? (CP.n[String(stockName).trim().toLowerCase()] || null) : null;
    var curG = !!(stock && CP.gs[stock]);
    var slot = function (c) {
      if (!c) return 0;
      if (stock && c === stock) return 0;
      if (CP.gs[c] || PMS_RE.test(c)) return curG ? 5 : 25;
      return curG ? 0 : 5;
    };
    var pct = Math.max(slot(p), a ? slot(a) : 0);
    var cents = Math.max(0, Math.round(unitCents || 0));
    var base = Math.floor(cents / 100);
    var v = base;
    if (pct) {
      v = Math.ceil((base * (1 + pct / 100)) / 20) * 20;
      if (v % 100 === 0) v += 20;
    }
    var d = v - base;
    var S = CP.sur || {};
    var ok = S.vid && d > 0 && d % S.unit === 0;
    var amt = ok && S.amounts ? S.amounts[String(d)] : 0;
    return { units: ok ? (amt ? 1 : d / S.unit) : 0, vid: amt || S.vid, price: cents + (ok ? d * 100 : 0) };
  }
  function loadComboPrice() {
    if (window.AGCF && typeof window.AGCF.comboPrice === "function") return Promise.resolve(true);
    if (CP_LOAD) return CP_LOAD;
    var url = cpUrl();
    if (!url) return Promise.resolve(false);
    CP_LOAD = fetch(url).then(function (r) { return r.ok ? r.json() : null; }).then(function (j) {
      if (!j || !j.c || !j.sur) return false;
      CP = { cs: {}, gs: {}, n: j.n || {}, sur: j.sur, sw: j.sw || {}, swb: j.swb || "" };
      j.c.forEach(function (c) { CP.cs[c] = 1; });
      (j.g || []).forEach(function (c) { CP.gs[c] = 1; });
      window.AGCF = window.AGCF || {};
      if (typeof window.AGCF.comboPrice !== "function") window.AGCF.comboPrice = comboPrice;
      return true;
    }, function () { CP_LOAD = null; return false; });
    return CP_LOAD;
  }

  /* PDP polish (Ansen 2026-10-17, boards 2A/2D): a dot of each picked finish before the set cards' finish line. A PANTONE
     code carries its screen color; a wood or solid finish shows its swatch picture, from agcf.js on a product page or
     from ag-combo-price.json (sw) elsewhere. Unknown codes draw no dot. */
  function swatch(code) {
    code = String(code || "");
    if (!code) return null;
    var m = /^PMS-[A-Za-z0-9_]+-([0-9A-Fa-f]{6})$/.exec(code);
    if (m) return { hex: "#" + m[1].toLowerCase(), image: "" };
    if (window.AGCF && typeof window.AGCF.swatch === "function") {
      var s = window.AGCF.swatch(code);
      if (s) return s;
    }
    var u = CP && CP.sw ? CP.sw[code] || "" : "";
    if (!u) return null;
    if (u.indexOf("://") < 0) u = (CP.swb || "") + u;
    return { hex: "", image: u + (u.indexOf("?") < 0 ? "?" : "&") + "width=48" };
  }
  function dots(codeStr, rendering) {
    var c = codes(codeStr), out = "";
    [c.p, c.a].forEach(function (k) {
      var s = k ? swatch(k) : null;
      if (!s) return;
      /* the style attribute is double-quoted, so the picture's url() takes single quotes; quotes and brackets in it go */
      var bg = s.image ? "url('" + String(s.image).replace(/['"\\<>]/g, "") + "')" : /^#[0-9a-f]{6}$/i.test(s.hex || "") ? s.hex : "";
      if (bg) out += '<i style="background:' + bg + ' center/cover no-repeat"></i>';
    });
    return out ? '<span class="ag-fdots' + (rendering ? " is-pulse" : "") + '" aria-hidden="true">' + out + "</span>" : "";
  }

  /* PDP polish round 4 (Ansen 2026-10-17, board 7A): a standard finish shows its dots too and names its accent ("Red
     Mahogany and Dark Slate Matte"). A variant's main and accent finish (name + swatch) come from the product's
     ag-finish-data view (templates/product.ag-finish-data.liquid), fetched once per product, kept for the session. */
  var FD = {}, FDP = {};
  function finFor(handle) {
    handle = String(handle || "");
    if (!handle) return Promise.resolve(null);
    if (FD[handle]) return Promise.resolve(FD[handle]);
    if (FDP[handle]) return FDP[handle];
    var sk = "agfd:" + handle;
    try { var s = sessionStorage.getItem(sk); if (s) { FD[handle] = JSON.parse(s); return Promise.resolve(FD[handle]); } } catch (e) { /* storage off */ }
    /* no Accept header: asked for application/json, Shopify answers the product record instead of this view (as
       cart-drawer.js _agChairData found) */
    FDP[handle] = fetch("/products/" + encodeURIComponent(handle) + "?view=ag-finish-data", { credentials: "same-origin" })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        if (!(j && j.v && typeof j.v === "object")) { FD[handle] = {}; return FD[handle]; }   // not the view: no dots, not kept
        FD[handle] = j.v;
        try { sessionStorage.setItem(sk, JSON.stringify(j.v)); } catch (e) { /* storage off */ }
        return j.v;
      }, function () { delete FDP[handle]; return null; });
    return FDP[handle];
  }
  function finSync(handle, vid) {
    var m = FD[String(handle || "")];
    var e = m ? m[String(vid)] : null;
    return e && e.length ? e : null;
  }
  function finNames(list) { return (list || []).map(function (x) { return x[0]; }).join(" and "); }
  function finDots(list, rendering) {
    var out = "";
    (list || []).forEach(function (x) {
      var u = String(x[1] || "").replace(/['"\\<>]/g, "");
      if (u) out += "<i style=\"background:url('" + u + "') center/cover no-repeat\"></i>";
    });
    return out ? '<span class="ag-fdots' + (rendering ? " is-pulse" : "") + '" aria-hidden="true">' + out + "</span>" : "";
  }
  /* the cart lines' finish placeholders: <span class="ag-fslot" data-ag-codes="P=..;A=.."> (a custom finish: its dots)
     or data-ag-h + data-ag-v (a variant's own finishes: their dots, and the .ag-fname after it names both) */
  function fill(root) {
    var r = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(r.querySelectorAll(".ag-fslot:not([data-ag-done])"), function (el) {
      var codes = el.getAttribute("data-ag-codes");
      el.setAttribute("data-ag-done", "0");
      if (codes) {
        var put = function () { var d = dots(codes); if (d && el.isConnected) { el.innerHTML = d; el.setAttribute("data-ag-done", "1"); } return !!d; };
        if (!put() && !(window.AGCF && typeof window.AGCF.swatch === "function")) loadComboPrice().then(function (ok) { if (ok) put(); });
        return;
      }
      var h = el.getAttribute("data-ag-h"), v = el.getAttribute("data-ag-v");
      if (!h || !v) return;
      finFor(h).then(function () {
        var e = finSync(h, v);
        if (!e || !el.isConnected) return;
        el.innerHTML = finDots(e);
        el.setAttribute("data-ag-done", "1");
        var n = el.nextElementSibling;
        if (n && n.classList.contains("ag-fname")) n.textContent = finNames(e);
      });
    });
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", function () { fill(document); });
  else fill(document);

  /* 2g: the Finish preview ID of a key (same function as agcf.js a8FpId and the render service's fp_id) */
  function fpId(handle, p, a, side, width) {
    var s = [handle, p, a || 'none', side, width ? 'w' + parseInt(width, 10) : ''].join('|');
    var u = unescape(encodeURIComponent(s)), h = 0x811c9dc5;
    for (var i = 0; i < u.length; i++) { h ^= u.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
    var A = '23456789ABCDEFGHJKMNPQRSTVWXYZ', n = h % 729000000, out = '';
    for (var k = 0; k < 6; k++) { out = A.charAt(n % 30) + out; n = Math.floor(n / 30); }
    return 'FP-' + out;
  }
  function fpOfKey(key) {
    var x = String(key || '').split('__');
    if (x.length < 4 || !x[0] || !x[1]) return '';
    var w = /^w(\d+)$/.exec(x[4] || '');
    return fpId(x[0], x[1], x[2] === 'none' ? '' : x[2], x[3], w ? w[1] : '');
  }

  window.AGSR = { want: want, known: known, pending: pending, predict: predict, isReady: isReady, waitImage: waitImage,
                  choice: choice, rememberChoice: rememberChoice, loadComboPrice: loadComboPrice,
                  _comboPriceFromData: function () { return CP ? comboPrice : null; },
                  forHandle: forHandle, elapsed: elapsed, refused: function (o) { return !!o && given(keyOf(o)); },
                  codes: codes, inches: inches, sideOf: sideOf, keyOf: keyOf, perWidth: PER_WIDTH,
                  fpOfKey: fpOfKey, swatch: swatch, dots: dots,
                  finFor: finFor, finSync: finSync, finNames: finNames, finDots: finDots, fill: fill };
  try { window.dispatchEvent(new CustomEvent("agsr:boot")); } catch (e) { /* old browsers */ }
})();
